import React, { useState } from 'react';
import { X, Download, DollarSign, Building2, AlertCircle } from 'lucide-react';

interface WithdrawalRequestModalProps {
  onClose: () => void;
  availableBalance: number;
}

const WithdrawalRequestModal: React.FC<WithdrawalRequestModalProps> = ({
  onClose,
  availableBalance,
}) => {
  const [amount, setAmount] = useState('');

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('vi-VN').format(num) + 'đ';
  };

  const quickAmounts = [1000000, 10000000, 50000000];

  const handleQuickSelect = (value: number) => {
    setAmount(value.toString());
  };

  const handleSubmit = () => {
    // Handle withdrawal request
    console.log('Withdrawal amount:', amount);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 border-b border-blue-100 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Download className="w-5 h-5 text-white" />
            </div>
            <h2 className="!text-lg !font-semibold !text-gray-900 dark:!text-white !m-0">
              Yêu cầu rút tiền
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/60 dark:hover:bg-gray-700 rounded-lg transition-colors border-none bg-transparent cursor-pointer"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400 hover:text-red-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800/50 shadow-sm overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-400/10 rounded-full blur-3xl"></div>
            <p className="!text-xs !font-medium !text-blue-600 dark:!text-blue-400 !mb-1.5 !uppercase !tracking-wider !m-0 relative z-10">
              Số dư khả dụng
            </p>
            <p className="!text-2xl !font-bold !text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 !m-0 relative z-10">
              {formatCurrency(availableBalance)}
            </p>
          </div>

          {/* Bank Account Info */}
          <div className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-lg p-3.5 space-y-2.5">
            <p className="!text-xs !font-semibold !text-gray-900 dark:!text-white !m-0 !uppercase !tracking-wider flex items-center gap-2">
              Tài khoản nhận tiền
            </p>

            <div className="flex items-start gap-2.5 pl-2 mt-1">
              <DollarSign className="w-4 h-4 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <p className="!text-xs !text-gray-500 dark:!text-gray-400 !m-0 !mb-0.5">Số tài khoản</p>
                <p className="!text-sm !font-semibold !text-gray-900 dark:!text-white !m-0">
                  19036698686868
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2.5 pl-2">
              <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
              <div className="flex-1">
                <p className="!text-xs !text-gray-500 dark:!text-gray-400 !m-0 !mb-0.5">Ngân hàng</p>
                <p className="!text-xs !text-gray-900 dark:!text-white !m-0">
                  Vietcombank
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="!block !text-sm !font-medium !text-gray-900 dark:!text-white !mb-2 !m-0">
              Số tiền cần rút <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Nhập số tiền cần rút"
                value={amount}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setAmount(value);
                }}
                className="w-full px-4 py-2.5 pr-10 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:placeholder:text-gray-200 text-gray-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:!text-gray-200 text-sm">
                đ
              </span>
            </div>
          </div>

          <div>
            <p className="!text-sm !font-medium !text-gray-700 dark:!text-gray-300 !mb-2 !m-0">
              Chọn nhanh:
            </p>
            <div className="grid grid-cols-3 gap-2">
              {quickAmounts.map((value) => (
                <button
                  key={value}
                  onClick={() => handleQuickSelect(value)}
                  className="px-2.5 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 hover:bg-blue-50 dark:hover:border-blue-500 dark:hover:bg-blue-900/20 transition-all text-xs font-semibold"
                >
                  {value >= 1000000 ? `${value / 1000000}M` : formatCurrency(value)}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-3 border-l-4 border-amber-400 dark:border-amber-500">
            <div className="flex items-start gap-2 !mb-2">
              <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="!text-sm !font-semibold !text-amber-900 dark:!text-amber-300 !m-0">
                Lưu ý quan trọng
              </p>
            </div>
            <ul className="space-y-1 !text-xs !text-amber-800 dark:!text-amber-400 pl-6">
              <li className="!m-0">• Số tiền rút tối thiểu: 1.000.000đ</li>
              <li className="!m-0">• Thời gian xử lý: 1-3 ngày làm việc</li>
              <li className="!m-0">• Vui lòng kiểm tra kỹ thông tin</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2.5 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900/50 dark:to-gray-900/30">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg !bg-white dark:!bg-gray-800 !text-gray-700 dark:!text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 transition-all font-medium text-sm"
          >
            Hủy
          </button>
          <button
            onClick={handleSubmit}
            disabled={!amount || parseInt(amount) < 1000000}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed !text-white rounded-lg transition-all font-semibold border-none cursor-pointer shadow-lg shadow-blue-500/30 disabled:shadow-none text-sm"
          >
            Xác nhận
          </button>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalRequestModal;
